import { useState } from "react";
import type { ForumPost } from "../api/forumApi";
import { formatRelativeTime } from "../utils/time";
import { ForumAvatar, ForumText } from "./ForumText";
import { confirmDanger } from "./Dialog";

export interface PostNodeHandlers {
  onReply: (parentId: string, body: string) => Promise<void>;
  onModerate: (action: string, payload: Record<string, unknown>) => Promise<void>;
  onReport: (postId: string) => Promise<void>;
}

interface ForumPostNodeProps extends PostNodeHandlers {
  post: ForumPost;
  childrenByParent: Map<string, ForumPost[]>;
  depth: number;
  isAdmin: boolean;
  locked: boolean;
  canPost: boolean;
}

// Indentation stops mattering (and starts breaking phones) past a few
// levels, so deeper replies keep nesting in the data but render flat.
const MAX_INDENT_DEPTH = 5;

/**
 * One post plus everything under it, rendered recursively — this is what
 * makes the board threaded rather than a flat wall of replies. Reply
 * boxes are per-post, so "reply to Dave's second point" lands under
 * Dave's second point instead of at the bottom.
 */
export function ForumPostNode({
  post,
  childrenByParent,
  depth,
  isAdmin,
  locked,
  canPost,
  onReply,
  onModerate,
  onReport,
}: ForumPostNodeProps) {
  const [replying, setReplying] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(post.body);
  const [error, setError] = useState<string | null>(null);

  const replies = childrenByParent.get(post.id) ?? [];
  const descendantCount = countDescendants(childrenByParent, post.id);

  async function submitReply() {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      await onReply(post.id, body);
      setDraft("");
      setReplying(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reply failed.");
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit() {
    const body = editDraft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await onModerate("edit-post", { postId: post.id, body });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Edit failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`forum-post forum-post--depth-${Math.min(depth, MAX_INDENT_DEPTH)}`}>
      <div className={`forum-post__card${post.deleted ? " forum-post__card--deleted" : ""}`}>
        <div className="forum-post__meta">
          <ForumAvatar name={post.author} isWallet={post.isWallet} />
          <span className="forum-post__author">{post.author || "anon"}</span>
          {post.isWallet && <span className="forum-post__badge" title="Posted with a connected WAX wallet">WAX</span>}
          <span className="forum-post__time">{formatRelativeTime(new Date(post.createdAt).getTime())}</span>
          {post.editedAt && <span className="forum-post__time">· edited</span>}
          {descendantCount > 0 && (
            <button className="link-button forum-post__collapse" onClick={() => setCollapsed((c) => !c)}>
              {collapsed ? `show ${descendantCount} ${descendantCount === 1 ? "reply" : "replies"}` : "collapse"}
            </button>
          )}
        </div>

        {post.deleted ? (
          <p className="forum-post__tombstone">
            [removed by a moderator]
            {isAdmin && post.body ? <span className="forum-post__tombstone-body"> — was: “{post.body}”</span> : null}
          </p>
        ) : editing ? (
          <div className="forum-composer forum-composer--inline">
            <textarea value={editDraft} onChange={(e) => setEditDraft(e.target.value)} rows={4} maxLength={5000} />
            <div className="forum-composer__actions">
              <button className="btn btn--primary btn--small" onClick={saveEdit} disabled={busy}>
                Save
              </button>
              <button className="btn btn--outline btn--small" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <ForumText text={post.body} />
        )}

        <div className="forum-post__actions">
          {!locked && canPost && !post.deleted && (
            <button className="link-button" onClick={() => setReplying((r) => !r)}>
              {replying ? "Cancel" : "Reply"}
            </button>
          )}
          {!post.deleted && (
            <button className="link-button forum-post__report" onClick={() => onReport(post.id)}>
              Report
            </button>
          )}
          {isAdmin && (
            <>
              <span className="forum-post__mod-sep">mod:</span>
              {post.deleted ? (
                <button className="link-button" onClick={() => onModerate("restore-post", { postId: post.id })}>
                  Restore
                </button>
              ) : (
                <button className="link-button" onClick={() => onModerate("delete-post", { postId: post.id })}>
                  Remove
                </button>
              )}
              <button
                className="link-button"
                onClick={() => {
                  setEditDraft(post.body);
                  setEditing(true);
                }}
              >
                Edit
              </button>
              {post.id !== "op" && (
                <button
                  className="link-button forum-post__danger"
                  onClick={async () => {
                    if (await confirmDanger(`Permanently delete this post and its ${descendantCount} replies?`)) {
                      onModerate("purge-post", { postId: post.id });
                    }
                  }}
                >
                  Purge
                </button>
              )}
              <button
                className="link-button forum-post__danger"
                onClick={async () => {
                  if (await confirmDanger(`Ban the name "${post.author}" from posting?`)) {
                    onModerate("ban-name", { value: post.author });
                  }
                }}
              >
                Ban name
              </button>
              {post.ipHash && (
                <button
                  className="link-button forum-post__danger"
                  title={`IP hash ${post.ipHash}`}
                  onClick={async () => {
                    if (await confirmDanger("Ban this poster's IP (hashed) from the forum?")) {
                      onModerate("ban-ip", { value: post.ipHash });
                    }
                  }}
                >
                  Ban IP
                </button>
              )}
            </>
          )}
        </div>

        {error && <div className="forum-inline-error">{error}</div>}

        {replying && (
          <div className="forum-composer forum-composer--inline">
            <textarea
              autoFocus
              rows={4}
              maxLength={5000}
              placeholder={`Replying to ${post.author || "anon"}…`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="forum-composer__actions">
              <button className="btn btn--primary btn--small" onClick={submitReply} disabled={busy || !draft.trim()}>
                {busy ? "Posting…" : "Post reply"}
              </button>
              <button className="btn btn--outline btn--small" onClick={() => setReplying(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {!collapsed &&
        replies.map((child) => (
          <ForumPostNode
            key={child.id}
            post={child}
            childrenByParent={childrenByParent}
            depth={depth + 1}
            isAdmin={isAdmin}
            locked={locked}
            canPost={canPost}
            onReply={onReply}
            onModerate={onModerate}
            onReport={onReport}
          />
        ))}
    </div>
  );
}

function countDescendants(childrenByParent: Map<string, ForumPost[]>, id: string): number {
  const kids = childrenByParent.get(id) ?? [];
  return kids.reduce((n, k) => n + 1 + countDescendants(childrenByParent, k.id), 0);
}
